import React, { useState, useContext, useCallback, useEffect, useRef } from "react";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import api from "../services/api";
import { FaHashtag, FaImage, FaSmile, FaGlobeAmericas, FaSpinner, FaLightbulb, FaTimes, FaArrowLeft, FaChevronRight } from "react-icons/fa";
import { getProfilePicture } from "../utils/avatar";
import { getPostSuggestionConfig } from "../data/post_suggestions";
import EmojiPicker from "./EmojiPicker";

const PostMessage = ({ onNewPost }) => {
  const { user } = useContext(AuthContext);
  const { condition, conditionConfig } = useContext(SurveyContext);
  const [content, setContent] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [showHashtagSuggestions, setShowHashtagSuggestions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  // Suggestion panel state (3-stage: stance → argument → tone)
  const [showSuggestionPanel, setShowSuggestionPanel] = useState(false);
  const [suggestionStage, setSuggestionStage] = useState(1); // 1=stance, 2=argument, 3=tone
  const [suggestionStance, setSuggestionStance] = useState(null); // "pro" | "contra"
  const [suggestionArgument, setSuggestionArgument] = useState(null); // argument id
  const [selectedSuggestion, setSelectedSuggestion] = useState(null); // tracks what was used for logging
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  const MAX_CHARS = 280;

  // Topic info from condition config
  const topic = conditionConfig?.topic;
  const topicName = topic?.name;

  // Build dynamic placeholder
  const placeholderVariants = topicName
    ? [
        `What do you think about ${topicName}?`,
        `Share your thoughts on ${topicName}...`,
        `What's your take on ${topicName}?`,
      ]
    : ["What's happening?"];

  // Stable placeholder per session (pick based on username hash)
  const placeholderIndex = user?.username
    ? user.username.charCodeAt(0) % placeholderVariants.length
    : 0;
  const placeholder = placeholderVariants[placeholderIndex];

  // 3-stage suggestion config (from static data, keyed by topic name)
  const suggestionConfig = React.useMemo(
    () => getPostSuggestionConfig(topicName),
    [topicName]
  );

  // Fetch hashtag suggestions
  const fetchHashtagSuggestions = useCallback(async () => {
    try {
      const response = await api.get("/search/trending", {
        params: { condition, limit: 8, timeframe: "24h" }
      });
      setHashtags(response.data.trending || []);
    } catch (error) {
      console.error("Error fetching hashtags:", error);
    }
  }, [condition]);

  useEffect(() => {
    fetchHashtagSuggestions();
  }, [fetchHashtagSuggestions]);

  // Handle hashtag insertion
  const insertHashtag = (hashtag) => {
    const newContent = content + (content.endsWith(' ') ? '' : ' ') + `#${hashtag} `;
    setContent(newContent);
    setShowHashtagSuggestions(false);
    textareaRef.current?.focus();
  };

  // Handle emoji insertion
  const insertEmoji = (emoji) => {
    const newContent = content + emoji;
    if (newContent.length <= MAX_CHARS) {
      setContent(newContent);
    }
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  };

  // Suggestion panel handlers (3-stage: stance → argument → tone)
  const handleOpenSuggestions = () => {
    setShowSuggestionPanel(true);
    setSuggestionStage(1);
    setSuggestionStance(null);
    setSuggestionArgument(null);
    logSuggestionEvent("panel_opened");
  };

  const handleSelectStance = (stance) => {
    setSuggestionStance(stance);
    setSuggestionStage(2);
    logSuggestionEvent("stance_selected", { stanceSelected: stance });
  };

  const handleSelectArgument = (argId) => {
    setSuggestionArgument(argId);
    setSuggestionStage(3);
    logSuggestionEvent("argument_selected", { stanceSelected: suggestionStance, argumentId: argId });
  };

  const handleSelectTone = (toneId) => {
    const text = suggestionConfig?.suggestions?.[suggestionStance]?.[suggestionArgument]?.[toneId];
    if (text) {
      setContent(text);
      setSelectedSuggestion({ stance: suggestionStance, argument: suggestionArgument, tone: toneId, text });
      logSuggestionEvent("suggestion_selected", {
        stanceSelected: suggestionStance,
        argumentId: suggestionArgument,
        toneId,
        originalText: text,
      });
    }
    setShowSuggestionPanel(false);
    setSuggestionStage(1);
    setSuggestionStance(null);
    setSuggestionArgument(null);
    textareaRef.current?.focus();
  };

  const handleSuggestionBack = () => {
    if (suggestionStage === 3) {
      setSuggestionStage(2);
      setSuggestionArgument(null);
    } else if (suggestionStage === 2) {
      setSuggestionStage(1);
      setSuggestionStance(null);
    }
  };

  const handleDismissSuggestions = () => {
    setShowSuggestionPanel(false);
    setSuggestionStage(1);
    setSuggestionStance(null);
    setSuggestionArgument(null);
    logSuggestionEvent("panel_dismissed");
  };

  const logSuggestionEvent = (action, data = {}) => {
    try {
      api.post("/suggestion-events", {
        action,
        condition,
        ...data,
      }).catch(() => {}); // Fire and forget
    } catch (e) {
      // Silent — don't block UI
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      setSelectedImage(file);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  // Remove selected image
  const removeImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Handle content changes
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    if (newContent.length <= MAX_CHARS) {
      setContent(newContent);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!content.trim() && !selectedImage) || loading) return;

    setLoading(true);
    try {
      let response;

      if (selectedImage) {
        // Create FormData for image upload
        const formData = new FormData();
        formData.append('content', content.trim());
        formData.append('condition', condition);
        formData.append('image', selectedImage);

        response = await api.post("/messages", formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Text-only post
        response = await api.post("/messages", {
          content: content.trim(),
          condition
        });
      }

      // Log suggestion edit if a suggestion was used
      if (selectedSuggestion) {
        const finalText = content.trim();
        const wasEdited = finalText !== selectedSuggestion.text;
        logSuggestionEvent("suggestion_posted", {
          stanceSelected: selectedSuggestion.stance,
          argumentId: selectedSuggestion.argument,
          toneId: selectedSuggestion.tone,
          originalText: selectedSuggestion.text,
          finalText,
          wasEdited,
        });
        setSelectedSuggestion(null);
      }

      // Clear form
      setContent("");
      removeImage();

      // Include the full author information in the new post
      const newPost = {
        ...response.data,
        author: response.data.author || {
          _id: user.userId,
          username: user.username,
        },
      };

      // First-post celebration
      const hasPostedBefore = sessionStorage.getItem("spiders_has_posted");
      if (!hasPostedBefore) {
        sessionStorage.setItem("spiders_has_posted", "true");
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 1500);
      }

      onNewPost(newPost); // Notify parent component about the new post
    } catch (error) {
      console.error("Error posting message:", error);
      alert("Error posting message");
    } finally {
      setLoading(false);
    }
  };


  const charCount = content.length;
  const isOverLimit = charCount > MAX_CHARS;
  const isNearLimit = charCount > MAX_CHARS * 0.8;

  return (
    <div className="border-b border-spiders-accent-400 border-opacity-30">
      <div className="p-4">
        <form onSubmit={handleSubmit}>
          <div className="flex space-x-3">
            <img
              src={getProfilePicture(user.username || "unknown", user.avatarSeed)}
              alt={user.username}
              className="w-11 h-11 rounded-full flex-shrink-0 ring-2 ring-spiders-border hover:ring-spiders-primary-500/50 transition-all cursor-pointer"
            />
            <div className="flex-1 min-w-0">
              {/* Textarea with focus styling */}
              <div className={`relative transition-all duration-200 ${content.length > 0 ? 'pb-2' : ''}`}>
                <textarea
                  ref={textareaRef}
                  value={content}
                  onChange={handleContentChange}
                  placeholder={placeholder}
                  className="w-full bg-transparent text-spiders-text-primary text-[15px] placeholder-spiders-text-muted/70 border-none outline-none resize-y min-h-[60px] max-h-[300px] leading-relaxed"
                  rows="2"
                />

                {/* First-post confetti */}
                {showConfetti && (
                  <div className="confetti-container absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="confetti-dot" />
                    <div className="confetti-dot" />
                    <div className="confetti-dot" />
                    <div className="confetti-dot" />
                    <div className="confetti-dot" />
                    <div className="confetti-dot" />
                    <div className="text-spiders-accent-400 text-sm font-medium animate-fadeIn">
                      Your first post!
                    </div>
                  </div>
                )}
              </div>

              {/* Suggestion panel trigger moved to action bar below */}

              {/* 3-stage suggestion panel */}
              {showSuggestionPanel && suggestionConfig && (
                <div className="mb-3 border border-spiders-border rounded-xl bg-spiders-bg-surface overflow-hidden shadow-lg">
                  {/* Header with progress + back/close */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-spiders-bg-elevated/50 border-b border-spiders-border">
                    <div className="flex items-center gap-3">
                      {suggestionStage > 1 && (
                        <button type="button" onClick={handleSuggestionBack}
                          className="text-spiders-text-muted hover:text-spiders-text-primary transition-colors p-0.5">
                          <FaArrowLeft className="w-3 h-3" />
                        </button>
                      )}
                      <div className="flex items-center gap-2">
                        <FaLightbulb className="w-3.5 h-3.5 text-spiders-accent-400" />
                        <span className="text-xs font-medium text-spiders-text-primary">
                          {suggestionStage === 1 && "What's your position?"}
                          {suggestionStage === 2 && "What's your main argument?"}
                          {suggestionStage === 3 && "How do you want to say it?"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Stage indicator */}
                      <div className="flex gap-1">
                        {[1, 2, 3].map((s) => (
                          <div key={s} className={`w-1.5 h-1.5 rounded-full transition-all ${
                            s <= suggestionStage ? "bg-spiders-accent-400" : "bg-spiders-bg-elevated"
                          }`} />
                        ))}
                      </div>
                      <button type="button" onClick={handleDismissSuggestions}
                        className="text-spiders-text-muted hover:text-spiders-text-secondary p-0.5">
                        <FaTimes className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* Stage 1: Stance */}
                  {suggestionStage === 1 && (
                    <div className="p-3 space-y-2">
                      {Object.entries(suggestionConfig.stances).map(([stanceKey, stanceData]) => (
                        <button key={stanceKey} type="button"
                          onClick={() => handleSelectStance(stanceKey)}
                          className="w-full text-left px-4 py-3 rounded-lg border border-spiders-border hover:border-spiders-primary-500/50 hover:bg-spiders-primary-500/5 transition-all group">
                          <span className="text-sm font-medium text-spiders-text-primary group-hover:text-spiders-primary-300 transition-colors">
                            {stanceData.label}
                          </span>
                          <p className="text-xs text-spiders-text-muted mt-0.5">{stanceData.description}</p>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Stage 2: Argument */}
                  {suggestionStage === 2 && suggestionStance && (
                    <div className="p-3 grid grid-cols-2 gap-2">
                      {suggestionConfig.stances[suggestionStance]?.arguments.map((arg) => (
                        <button key={arg.id} type="button"
                          onClick={() => handleSelectArgument(arg.id)}
                          className="text-left px-3 py-3 rounded-lg border border-spiders-border hover:border-spiders-accent-400/50 hover:bg-spiders-accent-400/5 transition-all group">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-base">{arg.icon}</span>
                            <span className="text-xs font-medium text-spiders-text-primary group-hover:text-spiders-accent-300 transition-colors">
                              {arg.label}
                            </span>
                          </div>
                          <p className="text-[11px] text-spiders-text-muted leading-snug">{arg.description}</p>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Stage 3: Tone — shows preview of the generated post */}
                  {suggestionStage === 3 && suggestionStance && suggestionArgument && (
                    <div className="p-3 space-y-2">
                      {suggestionConfig.tones.map((tone) => {
                        const previewText = suggestionConfig.suggestions?.[suggestionStance]?.[suggestionArgument]?.[tone.id];
                        if (!previewText) return null;
                        return (
                          <button key={tone.id} type="button"
                            onClick={() => handleSelectTone(tone.id)}
                            className="w-full text-left px-4 py-3 rounded-lg border border-spiders-border hover:border-spiders-primary-500/40 hover:bg-spiders-primary-500/5 transition-all group">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-sm">{tone.icon}</span>
                              <span className="text-xs font-semibold text-spiders-text-primary">{tone.label}</span>
                              <span className="text-[10px] text-spiders-text-muted">{tone.description}</span>
                            </div>
                            <p className="text-xs text-spiders-text-secondary leading-relaxed italic">
                              "{previewText}"
                            </p>
                            <p className="text-[10px] text-spiders-accent-400 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              Tap to use — you can edit before posting
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Image upload removed — not used in study */}

              {/* Emoji picker */}
              {showEmojiPicker && (
                <EmojiPicker
                  onSelect={insertEmoji}
                  onClose={() => setShowEmojiPicker(false)}
                />
              )}

              {/* Action bar */}
              <div className="flex items-center justify-between pt-3 border-t border-spiders-accent-400 border-opacity-30">
                <div className="flex items-center gap-1">
                  {/* Emoji toggle */}
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className={`p-2.5 rounded-full transition-all duration-200 ${
                      showEmojiPicker
                        ? 'text-spiders-primary-400 bg-spiders-primary-500/20'
                        : 'text-spiders-accent-400 hover:bg-spiders-accent-500/20 hover:text-spiders-accent-300 hover:scale-110'
                    }`}
                    title="Add emoji"
                  >
                    <FaSmile className="w-5 h-5" />
                  </button>

                  {/* Need inspiration — same level as emoji */}
                  {!showSuggestionPanel && suggestionConfig && (
                    <button
                      type="button"
                      onClick={handleOpenSuggestions}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-spiders-accent-400 hover:bg-spiders-accent-500/20 hover:text-spiders-accent-300 transition-all duration-200"
                    >
                      <FaLightbulb className="w-4 h-4" />
                      <span className="text-xs font-medium">Need inspiration?</span>
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {content.length > 0 && (
                    <span className={`text-sm font-medium ${
                      isOverLimit ? 'text-spiders-error' :
                      isNearLimit ? 'text-spiders-warning' :
                      'text-spiders-text-muted'
                    }`}>
                      {MAX_CHARS - charCount}
                    </span>
                  )}

                  <button
                    type="submit"
                    disabled={!content.trim() || isOverLimit || loading}
                    className={`px-5 py-2 rounded-full font-bold text-sm transition-all duration-200 ${
                      !content.trim() || isOverLimit || loading
                        ? 'bg-spiders-primary-500/30 text-white/40 cursor-not-allowed'
                        : 'bg-spiders-primary-500 hover:bg-spiders-primary-400 hover:shadow-lg hover:shadow-spiders-primary-500/25 text-white active:scale-95'
                    }`}
                  >
                    {loading ? (
                      <FaSpinner className="animate-spin w-4 h-4" />
                    ) : (
                      'Post'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostMessage;
